import React, { useCallback, useEffect, useMemo, useState } from 'react';
import aulaLinksService from '../../services/AulaLinksService';
import './AulaLinksPanel.css';

const normalizeLink = (item) => ({
  id: item?.id || item?.Id || '',
  idAula: item?.idAula || item?.IdAula || '',
  url: item?.url || item?.Url || '',
  descripcion: item?.descripcion || item?.Descripcion || '',
  activo: typeof item?.activo === 'boolean' ? item.activo : typeof item?.Activo === 'boolean' ? item.Activo : true,
  fechaAlta: item?.fechaAlta || item?.FechaAlta || null,
  fechaModificacion: item?.fechaModificacion || item?.FechaModificacion || null,
  idUsuarioModificacion: item?.idUsuarioModificacion || item?.IdUsuarioModificacion || null
});

const emptyForm = {
  url: '',
  descripcion: ''
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getShortGuid = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '—';
  return normalized.length > 12 ? `${normalized.slice(0, 8)}...${normalized.slice(-4)}` : normalized;
};

const AulaLinksPanel = ({ aulaId, service = aulaLinksService }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState({ ...emptyForm });
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [deleteCandidateId, setDeleteCandidateId] = useState('');

  const loadLinks = useCallback(async () => {
    if (!aulaId) return;

    setLoading(true);
    setError('');
    try {
      const response = await service.list(aulaId);
      const items = response?.items || response?.Items || response || [];
      setLinks(Array.isArray(items) ? items.map(normalizeLink) : []);
    } catch (e) {
      setError(e.message || 'No pudimos cargar los links del aula.');
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [aulaId, service]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const filteredLinks = useMemo(() => {
    const term = normalizeText(search);
    if (!term) return links;

    return links.filter((link) => normalizeText([
      link.url,
      link.descripcion,
      link.id
    ].join(' ')).includes(term));
  }, [links, search]);

  const selectedLink = useMemo(
    () => links.find((link) => link.id === selectedId) || null,
    [links, selectedId]
  );

  const assignedCount = links.length;

  const resetForm = useCallback(() => {
    setSelectedId('');
    setForm({ ...emptyForm });
    setDeleteCandidateId('');
  }, []);

  const handleEdit = useCallback((link) => {
    setSelectedId(link.id);
    setDeleteCandidateId('');
    setForm({
      url: link.url || '',
      descripcion: link.descripcion || ''
    });
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!aulaId) return;

    setSaving(true);
    setError('');
    try {
      if (selectedLink) {
        await service.update(aulaId, selectedLink.id, form);
      } else {
        await service.create(aulaId, form);
      }

      await loadLinks();
      resetForm();
    } catch (e) {
      setError(e.message || 'No pudimos guardar el link.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (linkId) => {
    if (!aulaId || !linkId) return;

    setDeletingId(linkId);
    setError('');
    try {
      await service.delete(aulaId, linkId);
      await loadLinks();
      if (selectedId === linkId) {
        resetForm();
      }
      setDeleteCandidateId('');
    } catch (e) {
      setError(e.message || 'No pudimos quitar el link del aula.');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div className="aula-links-panel">
      <div className="aula-links-toolbar">
        <div className="aula-links-search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por descripción, URL o GUID"
          />
        </div>

        <div className="aula-links-toolbar-actions">
          <div className="aula-links-summary">
            <span>{assignedCount} links activos</span>
          </div>
          <button type="button" className="btn btn-primary" onClick={resetForm} disabled={saving || loading}>
            <i className="fas fa-plus" />
            <span>Nuevo link</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="aula-links-alert">
          <i className="fas fa-exclamation-triangle" />
          <span>{error}</span>
        </div>
      )}

      <div className="aula-links-layout">
        <section className="aula-links-column aula-links-list-column">
          <div className="aula-links-section-header">
            <div>
              <h4>Links vinculados</h4>
              <p>Administrá los recursos externos disponibles para esta aula.</p>
            </div>
            <span className="aula-links-count">{filteredLinks.length}</span>
          </div>

          {loading ? (
            <div className="aula-links-empty">
              <i className="fas fa-spinner fa-spin" />
              <p>Cargando links...</p>
            </div>
          ) : filteredLinks.length === 0 ? (
            <div className="aula-links-empty">
              <i className="fas fa-link" />
              <p>No hay links cargados para esta aula con los filtros actuales.</p>
            </div>
          ) : (
            <div className="aula-links-list">
              {filteredLinks.map((link) => {
                const isSelected = selectedId === link.id;
                const isDeleting = deletingId === link.id;
                const isDeleteCandidate = deleteCandidateId === link.id;

                return (
                  <article
                    key={link.id}
                    className={`aula-links-card${isSelected ? ' selected' : ''}`}
                  >
                    <div className="aula-links-card-top">
                      <div className="aula-links-card-heading">
                        <span className="aula-links-card-icon">
                          <i className="fas fa-link" />
                        </span>
                        <div>
                          <strong>{link.descripcion || 'Link sin descripción'}</strong>
                          <small>{getShortGuid(link.id)}</small>
                        </div>
                      </div>

                      <div className="aula-links-card-actions">
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => handleEdit(link)}
                          disabled={saving || isDeleting}
                        >
                          <i className="fas fa-pen" />
                          <span>Editar</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline danger"
                          onClick={() => setDeleteCandidateId(isDeleteCandidate ? '' : link.id)}
                          disabled={saving || isDeleting}
                        >
                          <i className="fas fa-trash" />
                          <span>Quitar</span>
                        </button>
                      </div>
                    </div>

                    <a
                      className="aula-links-url"
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span>{link.url}</span>
                      <i className="fas fa-external-link-alt" />
                    </a>

                    <div className="aula-links-meta">
                      <span><strong>Alta:</strong> {formatDateTime(link.fechaAlta)}</span>
                      <span><strong>Modificación:</strong> {formatDateTime(link.fechaModificacion)}</span>
                    </div>

                    {isDeleteCandidate && (
                      <div className="aula-links-delete-confirm">
                        <div>
                          <strong>¿Quitar este link del aula?</strong>
                          <p>La asociación quedará inactiva y dejará de mostrarse como recurso.</p>
                        </div>
                        <div className="aula-links-delete-actions">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setDeleteCandidateId('')}
                            disabled={isDeleting}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => handleDelete(link.id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-trash" />}
                            <span>Confirmar</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <aside className="aula-links-column aula-links-form-column">
          <div className="aula-links-section-header">
            <div>
              <h4>{selectedLink ? 'Editar link' : 'Agregar link'}</h4>
              <p>
                {selectedLink
                  ? 'Actualizá la URL o la descripción del recurso seleccionado.'
                  : 'Cargá un nuevo recurso externo para que quede disponible en el aula.'}
              </p>
            </div>
          </div>

          <form className="aula-links-form" onSubmit={handleSubmit}>
            <label className="aula-links-field">
              <span>URL</span>
              <input
                type="url"
                value={form.url}
                onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))}
                placeholder="https://ejemplo.com/recurso"
                required
                disabled={saving}
              />
            </label>

            <label className="aula-links-field">
              <span>Descripción</span>
              <textarea
                rows="5"
                value={form.descripcion}
                onChange={(event) => setForm((current) => ({ ...current, descripcion: event.target.value }))}
                placeholder="Ejemplo: biblioteca digital, video explicativo o guía oficial"
                disabled={saving}
              />
            </label>

            {selectedLink && (
              <div className="aula-links-form-meta">
                <div>
                  <span>Fecha alta</span>
                  <strong>{formatDateTime(selectedLink.fechaAlta)}</strong>
                </div>
                <div>
                  <span>Fecha modificación</span>
                  <strong>{formatDateTime(selectedLink.fechaModificacion)}</strong>
                </div>
              </div>
            )}

            <div className="aula-links-form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
                disabled={saving}
              >
                {selectedLink ? 'Cancelar edición' : 'Limpiar'}
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <i className="fas fa-spinner fa-spin" /> : <i className={`fas ${selectedLink ? 'fa-save' : 'fa-plus'}`} />}
                <span>{selectedLink ? 'Guardar cambios' : 'Agregar link'}</span>
              </button>
            </div>
          </form>
        </aside>
      </div>
    </div>
  );
};

export default AulaLinksPanel;
